import {
  Currency,
  FUNDED_BY_NAME_MAX_LENGTH,
  ImpactGroup,
  ImpactShopProductQuickSelectOption,
  ImpactShopProductType,
  impactShopProductUnitTypePluralMapper,
  penceToDisplayPrice,
  ProductType,
  round,
  currencySymbols,
} from "@ecologi/shared";
import { isImpactFundProductType } from "@ecologi/shared/src";
import styled from "@emotion/styled";
import { useState } from "react";
import { Accordion, AccordionItem } from "../Accordion";
import { Button } from "../Button";
import { Heading } from "../Heading";
import { TextField, TextFieldChangeEvent } from "../TextField";
import { UnitTypeVisualTextMapper } from "../ImpactShopProductCard/ImpactShopProductCard";
import { TagImpactSection } from "./TagImpactSection";

const Outer = styled.div`
  background-color: ${({ theme }) => theme.colors.snowdrop};
  border-left: 1px solid ${({ theme }) => theme.colors.mistLight};

  @media ${({ theme }) => theme.bp.desktopUp} {
    grid-row-start: span 100;
  }
`;

const AddToBasket = styled(Button)`
  margin-bottom: 48px;
`;

const Container = styled.div`
  position: sticky;
  top: 80px;
  background-color: ${({ theme }) => theme.colors.snowdrop};
  padding: 34px 24px 48px 24px;
  font-size: 16px;

  @media ${({ theme }) => theme.bp.desktopUp} {
    padding: 48px 24px;
  }

  @media ${({ theme }) => theme.bp.wideDesktopUp} {
    padding: 48px 40px;
  }
`;

const FundedBy = styled.div`
  margin-bottom: 24px;
`;

const ManualQuantity = styled.div`
  margin-bottom: 30px;
`;

const QuickSelect = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
`;

const QuickSelectOption = styled.button<{
  active: boolean;
}>`
  all: unset;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.colors.mist};
  padding: 24px 6px;
  text-align: center;
  cursor: pointer;
  flex: 1;
  border: 2px solid
    ${(props) =>
      props.active ? props.theme.colors.forest : props.theme.colors.mist};
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  &:focus-visible {
    background: ${({ theme }) => theme.colors.mistDark};
    border-color: ${(props) =>
      props.active ? props.theme.colors.forest : props.theme.colors.mistDark};
  }
  &:hover:enabled {
    background-color: ${(props) =>
      props.active ? props.theme.colors.mist : props.theme.colors.mistDark};
    border: 2px solid
      ${(props) =>
        props.active ? props.theme.colors.forest : props.theme.colors.mistDark};
  }
`;

const QuickSelectOptionLabel = styled(Heading)`
  font-size: 16px !important;
  margin-bottom: 20px;
`;

const QuickSelectOptionPrice = styled.div`
  font-size: 14px;
`;

const SnippetText = styled.p`
  color: ${({ theme }) => theme.colors.charcoalLight};
  margin-bottom: 30px;
`;

const Title = styled(Heading)`
  margin-bottom: 20px;
`;

const UnitPrice = styled.p`
  font-weight: bold;
  margin-bottom: 24px;
  color: ${({ theme }) => theme.colors.charcoalLight};
`;

const getQuantityStep = (
  productType: ProductType,
  isImpactFundWithUnitType: boolean
): number => {
  if (productType === ProductType.CARBON_REMOVAL) {
    return 0.001;
  }
  if (productType === ProductType.HABITAT_RESTORATION) {
    return 0.1;
  }
  if (isImpactFundWithUnitType) {
    return 0.1;
  }
  return 1;
};

export type ImpactShopProductPageSidebarProps = {
  currency: Currency;
  groups: ImpactGroup[];
  impactGroupsEnabled: boolean;
  isLoggedIn: boolean;
  onAddToBasket: (
    product: ImpactShopProductType,
    quantity: number,
    fundedBy: string,
    groupId?: number
  ) => void;
  onCreateGroup: (name: string) => Promise<ImpactGroup>;
  product: ImpactShopProductType;
};

export const ImpactShopProductPageSidebar = ({
  currency,
  groups,
  impactGroupsEnabled,
  isLoggedIn,
  onAddToBasket,
  onCreateGroup,
  product,
}: ImpactShopProductPageSidebarProps) => {
  const {
    display: { description, impact, snippet, standards, title, unitType },
    isOutOfStock,
    quickSelectOptions,
    maxQuantity,
    minQuantity,
    prices,
    productType,
  } = product;

  const [activeQuickSelect, setActiveQuickSelect] = useState<number | null>(
    null
  );

  const accordionItems: AccordionItem[] = [
    {
      heading: "Description",
      description,
    },
    ...(impact
      ? [
          {
            heading: "Impact",
            description: impact,
          },
        ]
      : []),
    ...(standards
      ? [
          {
            heading: "Certifications",
            description: `This product is verified by one or more of the following standards: ${standards!.join(
              ", "
            )}.`,
          },
        ]
      : []),
  ];
  const [fundedBy, setFundedBy] = useState("");
  const [groupId, setGroupId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState<number | undefined>(undefined);

  const handleGroupChange = (id: number | null) => {
    setGroupId(id);
  };

  const unitPrice = prices.find(
    (price) => price.currency.toLowerCase() === currency.toLowerCase()
  )!.amount;

  const isImpactFundProduct = isImpactFundProductType(productType);
  const hasQuickSelectOptions = quickSelectOptions.length > 0;
  const currencySymbol = currencySymbols[currency];
  const isImpactFundWithUnitType = !!(isImpactFundProduct && unitType);

  const manualQuantityInputLabel = isImpactFundProduct
    ? isImpactFundWithUnitType
      ? undefined
      : `Enter an Impact Fund contribution (${currencySymbol})`
    : `${hasQuickSelectOptions ? "Or enter" : "Enter"} a custom amount`;

  const manualQuantityInputPlaceholder =
    isImpactFundProduct && !isImpactFundWithUnitType
      ? undefined
      : `Minimum ${minQuantity} ${
          unitType
            ? minQuantity > 1
              ? impactShopProductUnitTypePluralMapper[unitType]
              : UnitTypeVisualTextMapper[unitType].text
            : ""
        }`;

  const manualQuantityInputHelpText =
    isImpactFundProduct && !isImpactFundWithUnitType
      ? `The minimum contribution to an Impact Fund is ${currencySymbol}${minQuantity}. This allows us to maximise your impact by meaningfully contributing to each project in the fund cohort.`
      : undefined;

  const handleAddToBasket = () => {
    onAddToBasket(product, quantity ?? 0, fundedBy, groupId ?? undefined);
  };

  const handleManualQuantityChange = (
    { target: { value } }: TextFieldChangeEvent,
    productType: ProductType
  ) => {
    if (value === "") {
      setQuantity(undefined);
      setActiveQuickSelect(null);
      return;
    }

    const numericValue = Number(value);
    if (isNaN(numericValue)) return;

    const quantity =
      productType === ProductType.CARBON_OFFSETS ||
      productType === ProductType.CARBON_REMOVAL ||
      productType === ProductType.HABITAT_RESTORATION ||
      isImpactFundWithUnitType
        ? numericValue
        : round(numericValue, 0);

    setQuantity(quantity);
    setActiveQuickSelect(null);
  };

  const handleQuickSelect = (
    option: ImpactShopProductQuickSelectOption,
    i: number
  ) => {
    setQuantity(option.quantity);
    setActiveQuickSelect(i);
  };

  return (
    <Outer>
      <Container>
        <Title as="h1" size={5}>
          {title}
        </Title>
        {isImpactFundProduct ? (
          <>
            <SnippetText>{`${snippet}.`}</SnippetText>
            {isImpactFundWithUnitType && (
              <UnitPrice data-testid="unit-price">
                {penceToDisplayPrice(unitPrice, currency)}/
                {unitType && unitType}
              </UnitPrice>
            )}
          </>
        ) : (
          <UnitPrice data-testid="unit-price">
            {penceToDisplayPrice(unitPrice, currency)}/
            {unitType && UnitTypeVisualTextMapper[unitType].jsx}
          </UnitPrice>
        )}
        {hasQuickSelectOptions ? (
          <QuickSelect data-testid="quick-select">
            {quickSelectOptions.map((option, i) => (
              <QuickSelectOption
                disabled={isOutOfStock}
                active={activeQuickSelect === i}
                key={i}
                onClick={() => !isOutOfStock && handleQuickSelect(option, i)}
              >
                <QuickSelectOptionLabel as="h3">
                  {option.label}
                </QuickSelectOptionLabel>
                <QuickSelectOptionPrice>
                  {penceToDisplayPrice(
                    option.quantity * unitPrice,
                    currency
                  ).replace(".00", "")}
                </QuickSelectOptionPrice>
              </QuickSelectOption>
            ))}
          </QuickSelect>
        ) : null}
        <ManualQuantity>
          <TextField
            data-testid="manual-quantity-input"
            label={manualQuantityInputLabel ?? ""}
            min={minQuantity}
            max={maxQuantity}
            isDisabled={isOutOfStock}
            name="manualQuantity"
            onChange={(e) => handleManualQuantityChange(e, productType)}
            placeholder={manualQuantityInputPlaceholder}
            helpText={manualQuantityInputHelpText}
            step={getQuantityStep(productType, isImpactFundWithUnitType)}
            type="number"
            value={
              quantity === undefined ||
              (quantity === 0 &&
                productType !== ProductType.CARBON_OFFSETS &&
                productType !== ProductType.CARBON_REMOVAL &&
                productType !== ProductType.HABITAT_RESTORATION &&
                !isImpactFundWithUnitType)
                ? ""
                : quantity
            }
          />
        </ManualQuantity>
        {impactGroupsEnabled ? (
          <TagImpactSection
            fundedBy={fundedBy}
            groupId={groupId}
            groups={groups}
            isLoggedIn={isLoggedIn}
            isOutOfStock={isOutOfStock}
            onCreateGroup={onCreateGroup}
            onFundedByChange={setFundedBy}
            onGroupChange={handleGroupChange}
          />
        ) : (
          <FundedBy>
            <TextField
              data-testid="funded-by-input"
              helpText="This name will be displayed on your impact tile. You can use this to share your impact with your network."
              label="Funded by"
              isDisabled={isOutOfStock}
              name="fundedBy"
              placeholder="Enter an optional 'funded by' tag"
              onChange={(e) => setFundedBy(e.target.value)}
              maxLength={FUNDED_BY_NAME_MAX_LENGTH}
              showCharacterCount={fundedBy.length > 0}
              value={fundedBy}
            />
          </FundedBy>
        )}
        <AddToBasket
          text={isOutOfStock ? "Out of stock" : "Add to basket"}
          primary
          disabled={
            isOutOfStock ||
            !quantity ||
            quantity < minQuantity ||
            quantity > maxQuantity ||
            fundedBy.length > FUNDED_BY_NAME_MAX_LENGTH
          }
          fullWidth
          onClick={handleAddToBasket}
        />
        <Accordion items={accordionItems} />
      </Container>
    </Outer>
  );
};
